import "@fontsource/inter/latin-400.css";
import "@fontsource/inter/latin-500.css";
import "@fontsource/inter/latin-600.css";
import "@fontsource/inter/latin-700.css";
import React, { useEffect, useMemo, useState, lazy, Suspense } from "react";
import { createRoot } from "react-dom/client";
import {
  ClerkProvider,
  SignIn,
  SignUp,
  Show,
  useAuth,
  useClerk,
} from "@clerk/react";
import { ApiContext, makeApi, ApiError, post } from "./api";
import { Logo } from "./ui";
import { Library } from "./Library";
const BoardView = lazy(() =>
  import("./board/BoardView").then((m) => ({ default: m.BoardView })),
);
import type { Session } from "../shared/model";
import "./styles.css";
type Config = {
  publishableKey: string;
  development: boolean;
  environment: string;
  version: string;
};
async function clearCaches() {
  for (const key of Object.keys(localStorage))
    if (key.startsWith("ply:")) localStorage.removeItem(key);
  for (const db of await indexedDB.databases())
    if (db.name?.startsWith("ply:"))
      await new Promise<void>((resolve) => {
        const request = indexedDB.deleteDatabase(db.name!);
        request.onsuccess = () => resolve();
        request.onerror = () => resolve();
        request.onblocked = () => resolve();
      });
}
function Routes({ s, signOut }: { s: Session; signOut: () => void }) {
  const [path, setPath] = useState(location.pathname);
  useEffect(() => {
    const handler = () => setPath(location.pathname);
    window.addEventListener("popstate", handler);
    return () => window.removeEventListener("popstate", handler);
  }, []);
  const go = (p: string) => {
    if (s.user.id === "public") {
      location.assign(p);
      return;
    }
    history.pushState({}, "", p);
    setPath(p);
  };
  const board = path.match(/^\/board\/([^/]+)$/),
    share = path.match(/^\/share\/([^/]+)$/);
  return board || share ? (
    <Suspense
      fallback={
        <div className="gate">
          <Logo />
          <p>Opening your canvas…</p>
        </div>
      }
    >
      <BoardView
        boardId={board?.[1]}
        publicToken={share?.[1]}
        session={s}
        go={go}
      />
    </Suspense>
  ) : (
    <Library session={s} go={go} signOut={signOut} />
  );
}
function Public() {
  const s: Session = {
    user: { id: "public", name: "Guest", email: "", role: "viewer" },
    orgId: "",
    development: false,
  };
  return (
    <ApiContext.Provider value={makeApi(async () => null)}>
      <Routes s={s} signOut={() => {}} />
    </ApiContext.Provider>
  );
}
function Authenticated() {
  const { getToken } = useAuth(),
    clerk = useClerk();
  const api = useMemo(() => makeApi(getToken), [getToken]);
  const [s, setS] = useState<Session>(),
    [error, setError] = useState("");
  useEffect(() => {
    const load = () =>
      api
        .request<Session>("/api/session")
        .then((v) => {
          setS(v);
          setError("");
        })
        .catch((e) => {
          setError(e.message);
          if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
            setS(undefined);
            void clearCaches();
          }
        });
    void load();
    const t = setInterval(() => void load(), 25000);
    return () => clearInterval(t);
  }, [api]);
  return (
    <ApiContext.Provider value={api}>
      {s ? (
        <Routes
          s={s}
          signOut={() => {
            setS(undefined);
            void api
              .request("/api/logout", post())
              .finally(() => clearCaches().then(() => clerk.signOut()));
          }}
        />
      ) : (
        <div className="gate">
          <Logo />
          <h1>
            {error ? "Your workspace is waiting." : "Opening your workspace…"}
          </h1>
          <p>{error || "Just a moment."}</p>
          {error && (
            <button
              className="button secondary"
              onClick={() =>
                void api
                  .request("/api/logout", post())
                  .finally(() => clearCaches().then(() => clerk.signOut()))
              }
            >
              Sign out
            </button>
          )}
        </div>
      )}
    </ApiContext.Provider>
  );
}
function Local() {
  const api = useMemo(() => makeApi(async () => null), []);
  const [s, setS] = useState<Session>();
  useEffect(() => {
    void api.request<Session>("/api/session").then(setS);
  }, [api]);
  return (
    <ApiContext.Provider value={api}>
      {s ? (
        <Routes
          s={s}
          signOut={() => void clearCaches().then(() => location.reload())}
        />
      ) : (
        <div className="gate">
          <Logo />
          <p>Opening Ply…</p>
        </div>
      )}
    </ApiContext.Provider>
  );
}
function AuthForms() {
  const [signup, setSignup] = useState(location.hash.startsWith("#/sign-up"));
  useEffect(() => {
    const update = () => setSignup(location.hash.startsWith("#/sign-up"));
    window.addEventListener("hashchange", update);
    return () => window.removeEventListener("hashchange", update);
  }, []);
  return signup ? (
    <SignUp routing="hash" signInUrl="/#/sign-in" forceRedirectUrl="/" />
  ) : (
    <SignIn routing="hash" signUpUrl="/#/sign-up" forceRedirectUrl="/" />
  );
}
function App() {
  const [config, setConfig] = useState<Config>(),
    [error, setError] = useState("");
  useEffect(() => {
    void fetch("/api/config")
      .then((r) => r.json() as Promise<Config>)
      .then(setConfig)
      .catch(() => setError("Unable to connect to Ply. Please refresh."));
  }, []);
  if (location.pathname.startsWith("/share/")) return <Public />;
  if (!config)
    return (
      <div className="gate">
        <Logo />
        <p>{error || "Opening Ply…"}</p>
      </div>
    );
  if (config.development) return <Local />;
  if (!config.publishableKey)
    return (
      <div className="gate">
        <Logo />
        <span className="eyebrow">RENN MEDIA</span>
        <h1>
          A little room for
          <br />
          your next big idea.
        </h1>
        <p>
          Your workspace is being connected to Clerk.
          <br />
          Login will be available once setup is complete.
        </p>
        <span className="pill">Ply · Setup in progress</span>
      </div>
    );
  return (
    <ClerkProvider
      publishableKey={config.publishableKey}
      afterSignOutUrl="/"
      appearance={{
        variables: {
          colorPrimary: "#315bff",
          borderRadius: "12px",
          fontFamily: "Inter, system-ui, sans-serif",
        },
      }}
    >
      <Show when="signed-in">
        <Authenticated />
      </Show>
      <Show when="signed-out">
        <div className="login">
          <div className="login-intro">
            <Logo />
            <span className="eyebrow">MADE FOR RENN MEDIA</span>
            <h1>
              A little room for
              <br />
              your next big idea.
            </h1>
            <p>
              Gather the references. Connect the dots.
              <br />
              Make something worth sharing.
            </p>
            <div className="login-art">
              <div className="art-note">
                What if…<span>↗</span>
              </div>
              <div className="art-card">
                The next
                <br />
                <b>big idea.</b>
              </div>
            </div>
          </div>
          <div className="login-form">
            <AuthForms />
          </div>
        </div>
      </Show>
    </ClerkProvider>
  );
}
createRoot(document.getElementById("root")!).render(<App />);
